import { z } from "zod";

export const BRAND_NAME_MAX_LENGTH = 100;
export const BRAND_DISPLAY_NAME_MAX_LENGTH = 100;
export const BRAND_KEY_MAX_LENGTH = 25;

/**
 * Coerces form / UI values to a native {@link File} for multipart upload.
 */
export function normalizeBrandLogoFile(value: unknown): File | undefined {
  if (value == null) return undefined;
  if (value instanceof File) return value;
  if (typeof FileList !== "undefined" && value instanceof FileList) {
    return value.length > 0 ? (value.item(0) ?? value[0]) : undefined;
  }
  if (
    typeof value === "object" &&
    value !== null &&
    "originFileObj" in value &&
    (value as { originFileObj?: unknown }).originFileObj instanceof File
  ) {
    return (value as { originFileObj: File }).originFileObj;
  }
  return undefined;
}

/** Create/update brand via REST multipart `/admin/brand` (camelCase fields). */
const UpdateBrandSchema = z
  .object({
    brandId: z.number().int().positive().nullable().optional(),
    organizationId: z.number().int().positive(),
    organizationKey: z.string().trim().min(1),
    brandKey: z
      .string()
      .trim()
      .min(1)
      .regex(/^[A-Z0-9]+(-[A-Z0-9]+)*$/, "Invalid brand key format"),
    brandName: z.string().trim().min(1).max(BRAND_NAME_MAX_LENGTH),
    brandDisplayName: z.string().trim().min(1).max(BRAND_DISPLAY_NAME_MAX_LENGTH),
    brandDescription: z.string().max(200).optional().nullable(),
    brandIsActive: z.boolean(),
    brandLogoFile: z.preprocess(
      (val) => normalizeBrandLogoFile(val),
      z.instanceof(File).optional(),
    ),
    brandLogoUrl: z.preprocess(
      (val) => {
        if (val == null || val === "") return undefined;
        return typeof val === "string" ? val.trim() : undefined;
      },
      z.string().max(2048).optional(),
    ),
    brandLogoRemove: z.boolean().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.brandLogoFile && data.brandLogoRemove) {
      ctx.addIssue({
        code: "custom",
        message: "Cannot remove the logo and upload a new file in the same save",
        path: ["brandLogoRemove"],
      });
    }
    if (data.brandLogoRemove && data.brandLogoUrl?.trim()) {
      ctx.addIssue({
        code: "custom",
        message: "Cannot remove the logo and send a logo URL in the same save",
        path: ["brandLogoUrl"],
      });
    }
  });

export type UpdateBrandDTOShape = z.infer<typeof UpdateBrandSchema>;

export class UpdateBrandDTO {
  readonly brandId?: number | null;
  readonly organizationId!: number;
  readonly organizationKey!: string;
  readonly brandKey!: string;
  readonly brandName!: string;
  readonly brandDisplayName!: string;
  readonly brandDescription?: string | null;
  readonly brandIsActive!: boolean;
  readonly brandLogoFile?: File;
  readonly brandLogoUrl?: string;
  readonly brandLogoRemove?: boolean;

  constructor(data: UpdateBrandDTOShape) {
    const validated = UpdateBrandSchema.parse(data);
    Object.assign(this, validated);
  }

  static validate(data: unknown) {
    return UpdateBrandSchema.safeParse(data);
  }

  /** Maps validated DTO → REST multipart body (camelCase, same field names as GraphQL). */
  toMultipartFormData(): FormData {
    const formData = new FormData();
    formData.append("organizationId", String(this.organizationId));
    formData.append("organizationKey", this.organizationKey);
    formData.append("brandKey", this.brandKey);
    formData.append("brandName", this.brandName);
    formData.append("brandDisplayName", this.brandDisplayName);
    formData.append("brandDescription", this.brandDescription?.trim() ?? "");
    formData.append("isActive", this.brandIsActive ? "true" : "false");

    if (this.brandId != null && this.brandId > 0) {
      formData.append("brandId", String(this.brandId));
    }

    const isEdit = this.brandId != null && this.brandId > 0;
    const logoFile = normalizeBrandLogoFile(this.brandLogoFile);
    const trimmedLogoUrl = this.brandLogoUrl?.trim() ?? "";
    const removeLogo = isEdit && this.brandLogoRemove === true;

    if (logoFile instanceof File) {
      formData.append("brandLogoUrl", logoFile, logoFile.name);
    } else if (removeLogo) {
      formData.append("brandLogoUrl", "");
    } else if (trimmedLogoUrl.length > 0) {
      formData.append("brandLogoUrl", trimmedLogoUrl);
    }

    return formData;
  }
}
